"use client";

import { useRouter } from "next/navigation";
import { ArrowRight, Info } from "lucide-react";
import BaseDialog from "@/components/Modals/BaseDialog";
import { NoxusAlert } from "@/components/UI/NoxusAlert";
import { usePermissions } from "@/hooks/usePermissions";
import { ORG_PERMISSIONS } from "@/constants/permissions";
import type { AdminOAuthSetupRequiredDetails } from "@/components/Views/LinkedAccounts/utils";

/** Explains why an OAuth connect was refused and routes admins to the
 *  provider-config page. Non-admins see the same explanation without a
 *  call-to-action (the admin needs to do the setup). */
export function AdminOAuthSetupRequiredDialog({
  open,
  onClose,
  details,
}: {
  open: boolean;
  onClose: () => void;
  details: AdminOAuthSetupRequiredDetails | null;
}) {
  const router = useRouter();
  const { hasPermission } = usePermissions();
  const canManageProviders = hasPermission(
    ORG_PERMISSIONS.PROVIDERS.MANAGE,
    "tenant"
  );

  if (!details) return null;

  const goToAdmin = () => {
    onClose();
    router.push("/settings/platform/integrations");
  };

  const providerLabel = (
    <span className="font-medium text-foreground">{details.providerKey}</span>
  );

  return (
    <BaseDialog
      title={`${details.integrationName} needs OAuth setup`}
      isOpen={open}
      onOpenChange={(v) => !v && onClose()}
      size="lg"
      footerActions={
        canManageProviders
          ? [
              {
                label: "Later",
                props: { variant: "ghost", onClick: onClose },
              },
              {
                label: (
                  <span className="inline-flex items-center gap-1">
                    Configure provider
                    <ArrowRight className="size-4" />
                  </span>
                ),
                props: { onClick: goToAdmin },
              },
            ]
          : [
              {
                label: "Close",
                props: { variant: "ghost", onClick: onClose },
              },
            ]
      }
    >
      <div className="flex flex-col gap-3 text-sm text-muted-foreground">
        <p>
          An administrator must create an OAuth app on the provider&rsquo;s site
          and configure its client ID and secret first.
        </p>
        <NoxusAlert
          size="xs"
          icon={Info}
          description={
            canManageProviders ? (
              <>
                Open{" "}
                <span className="font-medium text-foreground">
                  Admin → Integrations
                </span>{" "}
                to configure the {providerLabel} provider, then try connecting
                again.
              </>
            ) : (
              <>
                Ask a workspace administrator to configure the {providerLabel}{" "}
                provider under{" "}
                <span className="font-medium text-foreground">
                  Admin → Integrations
                </span>
                .
              </>
            )
          }
        />
      </div>
    </BaseDialog>
  );
}
